import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { CheckOutlined, CloseOutlined } from '@ant-design/icons'
import { Button, Input, Space, Typography } from 'antd'
import {
  attempt,
  find,
  get,
  isEmpty,
  isEqual,
  isError,
  isNil,
  omit,
  pick,
  size,
  values,
} from 'lodash'
import PropTypes from 'prop-types'

import { areEqualByProps } from '../../../core/utils'

import { APP_ROUTES, METADATA_TYPES } from '../../constants'
import { nodePropType } from '../../prop-types'
import { StyledAlert, StyledVerticalSpace } from '../styled'

import MetadataNodeTitleButtons from './metadata-node-title-buttons'
import SearchHighlightedSpan from './search-highlighted-span'
import TypographyLinkWrapper from './typography-link-wrapper'

const MetadataNodeTitle = ({
  disableLink,
  node,
  metadataType,
  searchValue,
  editable,
  addNode,
  deleteNode,
  renameNode,
  setDataForRenaming,
  setIsCurationEnabled,
  revertReplaceNode,
  dataForRenaming,
  isTreeDirty,
  computeNodeHighlight,
  nodeTitleContentRender,
  showRenameInput,
  getLink,
}) => {
  const [nodeName, setNodeName] = useState(node.name)

  const nodeMetadataType = useMemo(() => {
    if (dataForRenaming?.id === node.id) {
      return null
    }

    if (!isNil(node.type)) {
      return find(
        values(METADATA_TYPES),
        ({ valueSingular }) => valueSingular === node.type,
      )
    }

    return metadataType
  }, [dataForRenaming?.id, metadataType, node.id, node.type])

  const nodeLink = useMemo(
    () => (
      <Link
        to={getLink({ nodeMetadataType, node })}
        component={props => (
          <TypographyLinkWrapper
            target="_blank"
            {...props}
            disabled={disableLink}
          />
        )}
      >
        <SearchHighlightedSpan searchValue={searchValue}>
          {nodeName}
        </SearchHighlightedSpan>
      </Link>
    ),
    [disableLink, getLink, node, nodeMetadataType, nodeName, searchValue],
  )

  const nodeTitleContent = useMemo(() => {
    if (!isNil(nodeTitleContentRender)) {
      return nodeTitleContentRender(node, searchValue)
    }

    let bannerType = attempt(computeNodeHighlight, node)

    if (!isNil(node.replaceToken)) {
      bannerType = 'info'
    }

    if (isError(bannerType) || isNil(bannerType)) {
      return nodeLink
    }

    return (
      <StyledAlert
        showIcon
        message={nodeLink}
        banner={bannerType !== 'info'}
        type={bannerType}
      />
    )
  }, [
    nodeTitleContentRender,
    computeNodeHighlight,
    node,
    nodeLink,
    searchValue,
  ])

  const isRenameInputShown = useMemo(
    () => showRenameInput(dataForRenaming, node),
    [dataForRenaming, node, showRenameInput],
  )

  const handleConfirmRename = useCallback(() => {
    renameNode({ ...pick(node, 'path'), name: nodeName })
    setDataForRenaming({})
  }, [node, nodeName, renameNode, setDataForRenaming])

  const handleRevertRename = useCallback(() => {
    setNodeName(node.name)
    setDataForRenaming({})
  }, [node.name, setDataForRenaming])

  const handleOnRename = useCallback(event => {
    setNodeName(event.target.value)
  }, [])

  useEffect(() => {
    if (!isEmpty(node.name)) {
      setNodeName(node.name)
    }
  }, [node.name])

  return (
    <Space>
      {!isRenameInputShown ? (
        nodeTitleContent
      ) : (
        <>
          <StyledVerticalSpace>
            <Input
              style={
                isEmpty(nodeName) ? { border: '1px solid #ff4d4f' } : undefined
              }
              value={nodeName}
              placeholder="Input term..."
              onChange={handleOnRename}
            />
            {isEmpty(nodeName) && (
              <Typography.Text type="danger">
                Please input Name!
              </Typography.Text>
            )}
          </StyledVerticalSpace>
          <Button
            size="small"
            shape="circle"
            icon={<CheckOutlined />}
            onClick={handleConfirmRename}
          />
          <Button
            size="small"
            shape="circle"
            icon={<CloseOutlined />}
            onClick={handleRevertRename}
          />
        </>
      )}
      <MetadataNodeTitleButtons
        node={node}
        editable={editable}
        addNode={addNode}
        deleteNode={deleteNode}
        renameNode={renameNode}
        setDataForRenaming={setDataForRenaming}
        setIsCurationEnabled={setIsCurationEnabled}
        revertReplaceNode={revertReplaceNode}
        isTreeDirty={isTreeDirty}
        isRenameInputShown={isRenameInputShown}
      />
    </Space>
  )
}

MetadataNodeTitle.propTypes = {
  disableLink: PropTypes.bool.isRequired,
  metadataType: PropTypes.object.isRequired,
  node: nodePropType.isRequired,
  searchValue: PropTypes.string,
  editable: PropTypes.bool,
  addNode: PropTypes.func,
  deleteNode: PropTypes.func,
  renameNode: PropTypes.func,
  setDataForRenaming: PropTypes.func,
  setIsCurationEnabled: PropTypes.func,
  revertReplaceNode: PropTypes.func,
  dataForRenaming: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isTreeDirty: PropTypes.bool,
  computeNodeHighlight: PropTypes.func,
  nodeTitleContentRender: PropTypes.func,
  showRenameInput: PropTypes.func,
  getLink: PropTypes.func,
}

MetadataNodeTitle.defaultProps = {
  searchValue: '',
  editable: false,
  addNode: undefined,
  deleteNode: undefined,
  renameNode: undefined,
  setDataForRenaming: undefined,
  setIsCurationEnabled: undefined,
  revertReplaceNode: undefined,
  dataForRenaming: undefined,
  isTreeDirty: false,
  computeNodeHighlight: undefined,
  nodeTitleContentRender: undefined,
  showRenameInput: (dataForRenaming, node) => dataForRenaming?.id === node.id,
  getLink: ({ nodeMetadataType, node }) =>
    `${get(APP_ROUTES, `${get(nodeMetadataType, 'value')}.link`)}/${node.id}`,
}

export default memo(
  MetadataNodeTitle,
  ({ node: prevNode, ...restPrevious }, { node: nextNode, ...restNext }) =>
    isEqual(omit(prevNode, 'children'), omit(nextNode, 'children')) &&
    size(prevNode.children) === size(nextNode.children) &&
    areEqualByProps(restPrevious, restNext, [
      'searchValue',
      'dataForRenaming',
      'editable',
      'disableLink',
      'isTreeDirty',
    ]),
)
